import { Box, Button, Container, Header, SpaceBetween } from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { useApi } from '@/src/store/api.context';
import { useFlashbars } from '@/src/store/flashbar.context';

interface PARReportProps {
  eventName: string;
}

export const PARReport: React.FC<PARReportProps> = ({ eventName }) => {
  const { t } = useTranslation();
  const [generating, setGenerating] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const { genAiAPI } = useApi();
  const { addErrorFlashbar } = useFlashbars();

  // Cleanup blob URL when component unmounts or when a new URL is created
  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  const handleGenerateReport = async () => {
    setGenerating(true);
    setDownloadUrl(null); // Clear previous report

    try {
      // Get the download URL from the API
      const response = await genAiAPI.getAIPARReport(eventName);
      const s3Url = response?.download_url;

      if (!s3Url) {
        console.error('No download_url in response:', response);
        addErrorFlashbar(t(i18nKeys.report.parReport.errorGeneratingReport));
        return;
      }

      // Fetch the PDF as a blob to bypass S3 headers that prevent iframe embedding
      const pdfResponse = await fetch(s3Url);
      if (!pdfResponse.ok) {
        throw new Error(`Failed to fetch PDF: ${pdfResponse.statusText}`);
      }

      const blob = await pdfResponse.blob();

      // Create a blob URL that can be displayed in an iframe
      const blobUrl = URL.createObjectURL(blob);

      // Store the blob URL for display
      setDownloadUrl(blobUrl);
    } catch (apiError) {
      console.error('Failed to generate AI team report (API error):', apiError);
      addErrorFlashbar(t(i18nKeys.report.parReport.errorGeneratingReport));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!downloadUrl) return;

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `par-report-${eventName}.pdf`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <Container header={<Header variant="h2">{t(i18nKeys.report.parReport.title)}</Header>}>
        <SpaceBetween direction="vertical" size="s">
          <Box>{t(i18nKeys.report.parReport.description)}</Box>
          <Box>{t(i18nKeys.report.parReport.languageNote)}</Box>
        </SpaceBetween>
      </Container>

      <Button variant="primary" onClick={handleGenerateReport} disabled={generating} loading={generating}>
        {t(i18nKeys.report.parReport.generateButton)}
      </Button>

      {downloadUrl && (
        <Container>
          <SpaceBetween direction="vertical" size="m">
            <Box>
              <iframe
                src={downloadUrl}
                width="100%"
                height="842px"
                style={{ border: 'none', display: 'block' }}
                title="Performance Report PDF"
              />
            </Box>
            <Box textAlign="center">
              <Button iconName="download" onClick={handleDownload}>
                {t(i18nKeys.report.parReport.downloadButton)}
              </Button>
            </Box>
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
};
